/**
 * RestoID — sign-in code verification that registers unknown identifiers itself.
 * ---------------------------------------------------------------------------
 * A restatement of the upstream container hook
 * `src/containers/VerificationCode/use-sign-in-flow-code-verification.ts` with
 * exactly ONE behavioural change: when the code verifies but the identifier has
 * no account yet, upstream stops to ask "Аккаунт … не найден. Создать новый?"
 * and RestoID registers straight away.
 *
 * Why dropping that confirmation is safe HERE, and only here. The question is
 * reached only after the user has entered the code that was sent to that exact
 * identifier, so control of it is already proven. A mistyped number cannot get
 * this far — the code goes to the number as typed, so a typo simply never yields
 * a code anyone can enter. What the dialog still covered is narrower, and giving
 * it up is a deliberate trade rather than a free win: a number the user DOES
 * control but which is not the one their account sits on — a second SIM, a work
 * line, a number they moved away from. The server says only `user_not_exist`, so
 * nothing here can tell that person apart from a genuinely new one, and they now
 * land in a second, empty account instead of being asked. Accepted: one duplicate
 * account in a case the product cannot detect, against a step removed for every
 * real new user — and the tenant is `SignInAndRegister` with phone-only sign-up,
 * i.e. signing in and registering are one journey by design. If that trade is
 * revisited, the lever is telling the user a NEW account was just created, not
 * restoring the question.
 *
 * Kept from upstream, deliberately and unchanged:
 *   - the sign-up-disabled guard. If the tenant does not allow SIGN-UP by code
 *     for this identifier type there is no account for us to create, so
 *     upstream's alert still fires and still sends the user back. Registering
 *     past it would create accounts the tenant has switched off;
 *   - `termsValidation()` before registering, so an `agreeToTermsPolicy` of
 *     `Manual` or `ManualRegistrationOnly` is still enforced on this path. It is
 *     a no-op on the tenant as configured today (policy `Automatic`, and both
 *     terms URLs empty), and it is called anyway so that configuring terms in
 *     the console later re-arms the gate on its own, with no code change;
 *   - `navigate(-1)` when the terms are declined. Upstream's reason still holds:
 *     the code was consumed by the failed sign-in, so staying on this page would
 *     only let the user retry a dead code and hit `verification_code.not_found`;
 *   - every error handler, their precedence, and the `redirectTo` handling.
 *
 * Changed from upstream, and only these two things:
 *
 *  1. the `usePromiseConfirmModal` call and its `if (!confirmed)` branch are gone
 *     — the point of the file;
 *  2. `onSubmit` calls `errorCallback?.()` after `handleError`, which upstream
 *     does not do HERE. Upstream's `callback: errorCallback` key in the handler
 *     map is dead code: `ErrorHandlers` (`src/hooks/use-error-handler.ts`)
 *     declares only Logto error codes and `global`, and `handleError` looks up
 *     nothing else. So after a wrong or expired code the OTP boxes kept the old
 *     digits, and since `codeInput` never changed, the page's auto-submit effect
 *     could not re-fire either — the user had to clear six boxes by hand before
 *     retrying. This is Logto's bug rather than a choice: upstream's own
 *     container (`src/containers/VerificationCode/index.tsx:49`) passes exactly
 *     this callback expecting it to run, and upstream's
 *     `use-register-flow-code-verification` already compensates with the same
 *     explicit call. Fixing it here costs nothing at sync time — `custom/` is
 *     never re-extracted — and WHEN LOGTO FIXES IT UPSTREAM this line becomes a
 *     harmless double-clear that should simply be dropped on that sync.
 *
 * Wired in by `custom/pages/VerificationCode.tsx`, which substitutes this for the
 * `UserFlow.SignIn` arm of upstream's hook map.
 */
import { InteractionEvent, type VerificationCodeIdentifier } from '@logto/schemas';
import { useCallback, useMemo } from 'react';

import { identifyWithVerificationCode, registerWithVerifiedIdentifier } from '@/apis/experience';
import useGeneralVerificationCodeErrorHandler from '@/containers/VerificationCode/use-general-verification-code-error-handler';
import useIdentifierErrorAlert, {
  IdentifierErrorType,
} from '@/containers/VerificationCode/use-identifier-error-alert';
import useApi from '@/hooks/use-api';
import type { ErrorHandlers } from '@/hooks/use-error-handler';
import useErrorHandler from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import { useSieMethods } from '@/hooks/use-sie';
import useSubmitInteractionErrorHandler from '@/hooks/use-submit-interaction-error-handler';
import useTerms from '@/hooks/use-terms';

const useSignInAutoRegister = (
  identifier: VerificationCodeIdentifier,
  verificationId: string,
  errorCallback?: () => void
) => {
  const { termsValidation } = useTerms();
  const navigate = useNavigateWithPreservedSearchParams();
  const redirectTo = useGlobalRedirectTo();
  const { isVerificationCodeEnabledForSignUp } = useSieMethods();
  const handleError = useErrorHandler();
  const registerWithIdentifierAsync = useApi(registerWithVerifiedIdentifier);
  const asyncSignInWithVerificationCodeIdentifier = useApi(identifyWithVerificationCode);

  const { errorMessage, clearErrorMessage, generalVerificationCodeErrorHandlers } =
    useGeneralVerificationCodeErrorHandler();

  const preSignInErrorHandler = useSubmitInteractionErrorHandler(InteractionEvent.SignIn, {
    replace: true,
  });
  const preRegisterErrorHandler = useSubmitInteractionErrorHandler(InteractionEvent.Register, {
    replace: true,
  });

  const showIdentifierErrorAlert = useIdentifierErrorAlert();

  const identifierNotExistErrorHandler = useCallback(async () => {
    const { type, value } = identifier;

    // Upstream's guard, kept verbatim: with sign-up by code switched off for this
    // identifier type there is no account to create, so say so and go back.
    if (!isVerificationCodeEnabledForSignUp(type)) {
      void showIdentifierErrorAlert(IdentifierErrorType.IdentifierNotExist, type, value);

      return;
    }

    // Upstream asks the user to confirm creating an account at this point. RestoID
    // does not — see the file header. The terms gate below is upstream's and stays.
    if (!(await termsValidation())) {
      navigate(-1);

      return;
    }

    const [error, result] = await registerWithIdentifierAsync(verificationId);

    if (error) {
      await handleError(error, preRegisterErrorHandler);

      return;
    }

    if (result?.redirectTo) {
      await redirectTo(result.redirectTo);
    }
  }, [
    identifier,
    isVerificationCodeEnabledForSignUp,
    termsValidation,
    showIdentifierErrorAlert,
    registerWithIdentifierAsync,
    verificationId,
    handleError,
    preRegisterErrorHandler,
    redirectTo,
    navigate,
  ]);

  const errorHandlers = useMemo<ErrorHandlers>(
    () => ({
      'user.user_not_exist': identifierNotExistErrorHandler,
      ...generalVerificationCodeErrorHandlers,
      ...preSignInErrorHandler,
      callback: errorCallback,
    }),
    [
      errorCallback,
      identifierNotExistErrorHandler,
      preSignInErrorHandler,
      generalVerificationCodeErrorHandlers,
    ]
  );

  const onSubmit = useCallback(
    async (code: string) => {
      const [error, result] = await asyncSignInWithVerificationCodeIdentifier({
        verificationId,
        identifier,
        code,
      });

      if (error) {
        await handleError(error, errorHandlers);
        // Not upstream's: see (2) in the header. Empties the code row so the user
        // can retype instead of deleting six digits first.
        errorCallback?.();

        return;
      }

      if (result?.redirectTo) {
        await redirectTo(result.redirectTo);
      }
    },
    [
      asyncSignInWithVerificationCodeIdentifier,
      errorCallback,
      errorHandlers,
      handleError,
      identifier,
      redirectTo,
      verificationId,
    ]
  );

  return {
    errorMessage,
    clearErrorMessage,
    onSubmit,
  };
};

export default useSignInAutoRegister;
